import { useEffect, useState } from "react";

export const useLocationTracker = ({ onLocationUpdate, addLog, connectedCamera, socketRef }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState("Waiting for location...");

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus("Geolocation not supported by your browser");
      addLog?.("Geolocation not supported");
      return;
    }

    setLocationStatus("Getting location...");
    addLog?.("Starting real-time geolocation tracking");

    const options = {
      enableHighAccuracy: true,
      maximumAge: 10000,
      timeout: 15000
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };

        const hasChanged =
          !userLocation ||
          Math.abs(locationData.latitude - userLocation.latitude) > 0.00001 ||
          Math.abs(locationData.longitude - userLocation.longitude) > 0.00001;

        if (hasChanged) {
          setUserLocation(locationData);
          setLocationStatus("Location updated");
          addLog?.(`Location updated: ${locationData.latitude.toFixed(6)}, ${locationData.longitude.toFixed(6)}`);

          if (socketRef?.current?.connected && !connectedCamera) {
            socketRef.current.emit("user-location", locationData);
            addLog?.("Sent real location to server");
          }

          onLocationUpdate?.(locationData);
        }
      },
      (error) => {
        const messages = {
          1: "User denied the request for geolocation",
          2: "Location information unavailable",
          3: "Request timed out"
        };
        setLocationStatus(messages[error.code] || error.message);
        addLog?.(`Geolocation error: ${messages[error.code] || error.message}`);
      },
      options
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
      addLog?.("Stopped location tracking");
    };
  }, [userLocation, socketRef, connectedCamera]);

  return { userLocation, locationStatus };
};
